import React, { useEffect } from "react";
import OverviewCard from "../../../components/cards/OverviewCard";
import { Package, ShoppingBag, Users } from "lucide-react";
import LineChart from "../../../components/charts/LineChart";
import PieChart from "../../../components/charts/DoughnutChart";
import CourierCard from "../../../components/cards/CourierCard";
import WhiteTitledCard from "../../../components/cards/WhiteTitledCard";
import SummaryTable from "./SummaryTable";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import {
  clearOrders,
  fetchAllOrders,
} from "../../../redux/features/orderSlice";
import { fetchAllCouriers } from "../../../redux/features/CourrierSlice";
import { fetchUsersAsync } from "../../../redux/features/adminSlice";
import moment from "moment";

const Dashboard: React.FC = () => {
  const { orders, loading: orderLoading } = useAppSelector(
    (state: RootState) => state.orders
  );
  const { couriers, loading: courierLoading } = useAppSelector(
    (state) => state.courier
  );
  const { vendors, isLoading: vendorsLoading } = useAppSelector(
    (state) => state.admin
  );
  const { buyers, isLoading: buyersLoading } = useAppSelector(
    (state) => state.admin
  );
  const { user } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(clearOrders());
    if (
      user?.user?.roles === "COURIER_ADMIN" ||
      user?.user?.roles === "SUPER_ADMIN"
    ) {
      dispatch(
        fetchAllOrders({
          limit: 10,
          page: 1,
        })
      );
    }
    dispatch(fetchAllCouriers({ page: 1, limit: 8 }));
    dispatch(fetchUsersAsync({ page: 1, limit: 5, userType: "VENDOR" }));
    dispatch(fetchUsersAsync({ page: 1, limit: 5, userType: "BUYER" }));
  }, [dispatch, user]);

  return (
    <div className="p-4 bg-gray-100 min-h-screen space-y-8">
      {/* Overview Section */}
      <WhiteTitledCard cardTitle="Overview">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4  gap-4">
          <OverviewCard
            title="Contracts"
            value={orders?.data?.totalItems ?? 0}
            icon={Package}
            change="+3%"
            isPositive={true}
            iconBg="bg-bgRed"
            loadingColor="#CA2222"
            isLoading={orderLoading}
          />
          <OverviewCard
            title="Courier Agents"
            value={couriers?.data?.meta?.totalItems ?? 0}
            icon={Users}
            change="+2%"
            isPositive={true}
            iconBg="bg-bgBlue"
            loadingColor="#2C14DD"
            isLoading={courierLoading}
          />
          <OverviewCard
            title="Vendors"
            value={vendors?.data?.totalItems ?? 0}
            icon={ShoppingBag}
            change="-10%"
            isPositive={false}
            iconBg="bg-bgPurple"
            loadingColor="#3F2EC8"
            isLoading={vendorsLoading}
          />
          <OverviewCard
            title="Buyers"
            value={buyers?.data?.totalItems ?? 0}
            icon={Users}
            change="+5%"
            isPositive={true}
            iconBg="bg-bgBlack"
            loadingColor="#000"
            isLoading={buyersLoading}
          />
        </div>
      </WhiteTitledCard>
      {/* Analytics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <LineChart />
        <PieChart />
      </div>

      {/* Recent Courier Agents Section */}
      <WhiteTitledCard cardTitle="Recent Courier Agents">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {couriers?.data?.items?.map((courier: any) => (
            <CourierCard
              staffCount={moment(courier?.createdAt).format("Do MMM YYYY")}
              companyName={courier?.name}
              icon={Users}
              key={courier.id}
            />
          ))}
        </div>
      </WhiteTitledCard>

      {/* Vendors Table and Buyers*/}
      <SummaryTable />
    </div>
  );
};

export default Dashboard;
